import React, { useEffect, useMemo, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { BffAddressLookupResponse, BffAddressRetrieveResponse } from '@lucky7ventures/bff-types';
import { useFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import useDebounce from '@/hooks/useDebounce';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { useRegistrationActions } from '@/store/zustand/registration-store';
import BffApiService from '@/shared/bffApiService';
import { provinces } from '@/features/modal/modals/register/ontario/AddressInfoForm';
import AddressDropdownField from '@/features/modal/modals/register/ontario/address-field/AddressDropdownField';
import { isEmpty } from '@/shared/utility';

interface MappedAddressLookupData {
  label: string;
  value: string;
  isFinal: boolean;
  isParent?: boolean;
  id: string;
}

const mapAddressLookupData = (data: BffAddressLookupResponse) => {
  return data.map(({ text, isFinal, id }) => {
    return { label: text, value: text, isFinal, id, isParent: false };
  });
};

const AddressField = () => {
  const [activeOptions, setActiveOptions] = useState<MappedAddressLookupData[]>([]);
  const [backupOptions, setBackupOptions] = useState<MappedAddressLookupData[]>([]);
  const [focusDropdown, setFocusDropdown] = useState(false);
  const [searchedAddressValue, setSearchedAddressValue] = useState<string | undefined>(undefined);
  const [selectedAddressValue, setSelectedAddressValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchedAddressValue, 300);
  const intl = useIntl();
  const { request: addressLookupRequest, loading: addressLookupLoading } =
    useBffApiRequest<BffAddressLookupResponse>();
  const { request: addressRetrievalRequest, loading: addressRetrievalLoading } =
    useBffApiRequest<BffAddressRetrieveResponse>();
  const { updateFields } = useRegistrationActions();
  const locale = useSelector(selectLocale);
  const { setFieldValue } = useFormikContext();

  const countryCode = 'ca';
  const languageCode = locale === 'fr' ? 'cf' : 'ca';

  const visibleOptions = useMemo(() => {
    if (!isEmpty(activeOptions)) {
      return activeOptions.map(({ isFinal, id, isParent, ...option }) => {
        if (isFinal) {
          const finalOnClick = () => {
            addressRetrievalRequest({
              apiMethod: BffApiService.addressRetrieval,
              payload: {
                id: id,
              },
              successCallback: ({
                street1,
                houseNumber,
                city,
                postalCode,
                provinceCode,
                countryCode,
              }) => {
                const province = provinces.find(
                  ({ value }) => value === `${countryCode}-${provinceCode}`,
                );
                updateFields({
                  address: street1,
                  houseNumber,
                  city,
                  postCode: postalCode,
                  ...(province ? { provinceCode: province.value } : {}),
                });
                setFieldValue('address', street1);
                setFieldValue('houseNumber', houseNumber);
                setFieldValue('postCode', postalCode);
                setFieldValue('city', city);
                if (province) {
                  setFieldValue('provinceCode', province.value);
                }
              },
            });
          };

          return { ...option, finalOnClick };
        }

        if (isParent) {
          const endIcon = <ChevronLeftIcon width={10} height={10} className="text-brand-gray" />;
          const nestedOnClick = () => {
            setActiveOptions(backupOptions);
          };
          return { ...option, nestedOnClick, endIcon };
        }

        const endIcon = <ChevronRightIcon width={10} height={10} className="text-brand-gray" />;
        const nestedOnClick = () =>
          addressLookupRequest({
            apiMethod: BffApiService.addressLookup,
            payload: {
              search: selectedAddressValue,
              country: countryCode,
              language: languageCode,
              container: id,
            },
            successCallback: data => {
              const underlyingData = mapAddressLookupData(data);
              underlyingData.unshift({ ...option, isFinal, id, isParent: true });
              setActiveOptions(underlyingData);
            },
          });
        return { ...option, nestedOnClick, endIcon };
      });
    } else {
      return [];
    }
  }, [activeOptions, backupOptions, selectedAddressValue]);

  useEffect(() => {
    setFocusDropdown(false);
    if (debouncedSearchValue === '') {
      setActiveOptions([]);
      setBackupOptions([]);
    }

    if (debouncedSearchValue) {
      addressLookupRequest({
        apiMethod: BffApiService.addressLookup,
        payload: {
          search: debouncedSearchValue,
          country: countryCode,
          language: languageCode,
        },
        successCallback: data => {
          const addressLookupData = mapAddressLookupData(data);

          setActiveOptions([...addressLookupData]);
          setBackupOptions([...addressLookupData]);
          setSearchedAddressValue(debouncedSearchValue);
        },
      });
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (focusDropdown) {
      const elements = document.getElementsByClassName('focusable-element');
      (elements[0] as HTMLElement).focus();
    }
  }, [focusDropdown]);

  return (
    <div className="flex flex-col items-stretch gap-1">
      <span className="text-white">
        {intl.formatMessage({ id: 'registration.ontario.address.hint' })}
      </span>
      <AddressDropdownField
        loading={addressLookupLoading || addressRetrievalLoading}
        options={visibleOptions}
        data-cy="registration-address-info-address-dropdown"
        label={intl.formatMessage({ id: 'inputs.address' })}
        name="address"
        onUserInput={inputString => setSearchedAddressValue(inputString)}
        onSelect={selectedValue => setSelectedAddressValue(selectedValue)}
        onFocus={() => triggerGTag(GTagEvents.ont_reg_address_click)}
        onValidationError={error => triggerGTag(GTagEvents.ont_reg_address_error, { error })}
        labelClassName="text-white"
        selectorIconClassName="text-white"
        inputClassName="border-[1px] border-white/30 text-white focus-within:border-blue-blue"
      />
    </div>
  );
};

export default AddressField;
